import { useState, useEffect } from 'react';
import { Cake, Star } from 'lucide-react';
import { API } from '@/App';

export default function FamousBirthdays() {
  const [birthdays, setBirthdays] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBirthdays();
  }, []);

  const fetchBirthdays = async () => {
    try {
      const response = await fetch(`${API}/public/birthdays-today?limit=8`);
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      setBirthdays(data.birthdays || []);
      setDate(data.date || '');
    } catch (err) {
      console.error('Error fetching birthdays:', err);
      setError('Unable to load birthdays');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border border-[#334155] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Cake className="w-6 h-6 text-[#D4AF37]" />
          <h3 className="font-playfair text-lg text-white">Famous Birthdays Today</h3>
        </div>
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error || birthdays.length === 0) {
    return null; // Silently fail - don't show widget if no data
  }

  return (
    <div className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border border-[#334155] rounded-xl p-6">
      {/* Header - Centered */}
      <div className="flex flex-col items-center justify-center mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
            <Cake className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="text-center">
            <h3 className="font-playfair text-lg text-white">Famous Birthdays</h3>
            <p className="text-sm text-[#D4AF37]">{date}</p>
          </div>
          <Star className="w-5 h-5 text-[#D4AF37]/50" />
        </div>
      </div>

      {/* Birthday List - Grid layout for full width, centered items */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {birthdays.slice(0, 6).map((person, index) => (
          <div
            key={index}
            className="flex items-center justify-center gap-3 p-3 rounded-lg bg-[#0F172A]/50 border border-transparent"
          >
            {/* Thumbnail or Initial */}
            {person.thumbnail ? (
              <img 
                src={person.thumbnail} 
                alt={person.name}
                className="w-10 h-10 rounded-full object-cover border border-[#334155] flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30 flex-shrink-0">
                <span className="text-[#D4AF37] font-playfair font-bold">
                  {person.name?.charAt(0) || '?'}
                </span>
              </div>
            )}
            
            {/* Info - Centered text */}
            <div className="text-center">
              <p className="text-white font-medium text-sm">
                {person.name?.replace(/_/g, ' ')}
              </p>
              <p className="text-[#64748B] text-xs">
                {person.year && `Born ${person.year} • `}
                {person.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-[#334155]">
        <p className="text-center text-xs text-[#64748B]">
          Celebrating life's milestones, past and present
        </p>
      </div>
    </div>
  );
}
